/**
 * FeatureGuard Component
 *
 * Route protection component that controls access to pages based on feature flags.
 * Acts as a guard that either renders the child component or redirects to overview.
 *
 * @author Sabya
 * @date September 30, 2025
 *
 * Purpose:
 * - Protect routes based on backend feature configuration
 * - Provide seamless UX with loading states
 * - Automatically redirect disabled features to overview page
 * - Prevent unauthorized access to disabled features
 *
 * How It Works:
 * 1. Checks if feature flag API is loading → Shows spinner
 * 2. Checks if feature is enabled → Renders children
 * 3. If feature is disabled → Redirects to overview page
 *
 * Usage Example in App.jsx:
 *   <Route
 *     path="concept-analysis"
 *     element={
 *       <FeatureGuard feature="conceptAnalysis">
 *         <ConceptAnalysisPage />
 *       </FeatureGuard>
 *     }
 *   />
 *
 * Supported Features (8 total):
 * - conceptAnalysis
 * - successFactors
 * - personaAnalysis
 * - comparingConcepts
 * - scores
 * - idealConcept
 * - purchaseDrivers (future)
 * - radar (future)
 *
 * Security Note:
 * This is a UX-level guard. Backend APIs must still validate permissions.
 * Feature flags are not a security mechanism, just user experience control.
 */

import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectFeatures, selectLoading } from '../redux/slices/featureSlice';

/**
 * FeatureGuard Functional Component
 *
 * @param {Object} props
 * @param {string} props.feature - Feature flag name to check (e.g., 'conceptAnalysis')
 * @param {React.ReactNode} props.children - Component to render if feature is enabled
 * @param {string} [props.redirectTo] - Optional custom redirect path (default: concept-report)
 *
 * @returns {React.ReactNode} Loading spinner, redirect, or children
 */
const FeatureGuard = ({ feature, children, redirectTo }) => {
    // Fetch feature flags from Redux store
    const features = useSelector(selectFeatures);

    // Check if API call is in progress
    const loading = useSelector(selectLoading);

    // Extract URL parameters for redirect path construction
    const { projectId, runId } = useParams();

    // =================================================================
    // STATE 1: Loading - API call in progress
    // =================================================================
    // While fetching feature configuration, show loading spinner
    // to prevent flash of unauthorized content
    if (loading) {
        return (
            <div className='w-full h-screen flex justify-center items-center'>
                <div className="h-12 w-12 border-[4px] border-[#4F46E5]
                              border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    // =================================================================
    // STATE 2: Feature Disabled - Redirect to overview
    // =================================================================
    // If the requested feature is disabled in the config,
    // redirect user to the overview page (concept-report)
    // Uses 'replace' to prevent back button issues
    if (!features[feature]) {
        const defaultRedirect = `/${projectId}/${runId}/concept-report`;
        return <Navigate to={redirectTo || defaultRedirect} replace />;
    }

    // =================================================================
    // STATE 3: Feature Enabled - Render children
    // =================================================================
    // Feature is enabled, render the protected component
    return <>{children}</>;
};

export default FeatureGuard;